import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable, type Signal, signal } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator } from '@common/enums';
import {
  PageDriveItemsPayload,
  PageSiteItemsPayload,
  RestoreSiteItemsPayload,
  SearchODataOptions,
  SearchType,
  SetupSitesBackupPayload,
  Site,
  SiteDriveOdataItem,
  SiteItemDetails
} from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { fetchItemDetailsHelper } from '@common/odata/utils';
import { SharedOdataService } from '@common/services';
import { SharePointTag, SharePointTagsMap } from '@common/services/smart-search/sharePoint';
import { filterByWords, getDefaultPaginationOptions } from '@common/utils';
import { containsWrapper } from '@common/utils/functions/search';
import { SmartSearchHelper } from '@common/utils/helper/smart-search.helper';
import { SmartSearchModel } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const ORDER_BY_DEFAULT = 'BackupOn desc, Name asc';
const COUNT_KEY = '@odata.count';

@Injectable({ providedIn: 'root' })
export class SiteItemsODataService extends SharedOdataService<SiteDriveOdataItem> {
  private readonly http = inject(HttpClient);

  private odataMethods = this.odataFactory.CreateService<any>('');
  private odataSiteLists = this.odataFactory.CreateService<SiteDriveOdataItem>('SiteLists');
  private odataSiteListsItems = this.odataFactory.CreateService<SiteDriveOdataItem>('SiteListItems');
  private odataSiteDriveItems = this.odataFactory.CreateService<SiteDriveOdataItem>('SiteDriveItems');
  private odataSite = this.odataFactory.CreateService<Site>('Sites');

  public readonly defaultPaginationOptions = getDefaultPaginationOptions();

  readonly #fileName = SharePointTagsMap.get(SharePointTag.FileName);
  readonly #mixinName = SharePointTagsMap.get(SharePointTag.Mixin);
  readonly #siteName = SharePointTagsMap.get(SharePointTag.SiteName);

  readonly #dateTime = signal<string>(null);

  get dateTime(): Signal<string> {
    return this.#dateTime.asReadonly();
  }

  set dateTime(value: string) {
    this.#dateTime.set(value);
  }

  constructor() {
    super('SiteItems');
  }

  getSiteItems<T>({ top = 50, skip = 0, orderBy = ORDER_BY_DEFAULT, filter = null } = {}): Observable<ODataPagedResult<T>> {
    return this.odataFactory.CreateService<T>('SiteItems').Query().Top(top).Skip(skip).OrderBy(orderBy).Filter(filter).ExecWithCount();
  }

  // TODO incorrect return's type. Correct is `SiteOdataItem`
  siteItems(options?: SearchODataOptions): Observable<ODataPagedResult<SiteDriveOdataItem>> {
    const { filter, orderBy, page, pageSize } = options || {};

    return this.odataFactory
      .CreateService<SiteDriveOdataItem>('SiteItems/GetActiveSiteItems')
      .Query()
      .Top(pageSize || this.pageSize)
      .Skip((pageSize || this.pageSize) * ((page || this.page) - 1))
      .OrderBy(this.orderByForSearch(orderBy) || ORDER_BY_DEFAULT) // must be `orderByGlobal`
      .Filter(filter || this.filter)
      .ExecWithCount();
  }

  totalSelectedSiteItems(): Observable<number> {
    const odataService = this.odataFactory.CreateService(`/SiteItems?$top=0&$count=true&filter=BackupOn eq true`);

    return this.http.get(odataService.Query().GetUrl()).pipe(
      catchError(() => [{ COUNT_KEY: 0 }]),
      map((res) => res?.[COUNT_KEY] ?? 0)
    );
  }

  listsOfSiteItems(blockId: string, id?: string): Observable<ODataPagedResult<SiteDriveOdataItem>> {
    const newOdata = this.odataFactory.CreateService<SiteDriveOdataItem>(`/SiteItems(${blockId})/Lists`);

    return newOdata
      .Query()
      .Top(this.pageSize)
      .Skip(this.pageSize * (this.page - 1))
      .OrderBy(this.orderBy) // must be `orderByGlobal`
      .CustomQueryOptions([{ key: 'Id', value: id }])
      .ExecWithCount();
  }

  driveItemsOfSiteLists(blockId: string, id: string): Observable<ODataPagedResult<SiteDriveOdataItem>> {
    const newOdata = this.odataFactory.CreateService<SiteDriveOdataItem>(`/SiteLists(${blockId})/DriveItems`);
    const customQueryOptions = [{ key: 'Id', value: id }];
    const dateTime = this.dateTime();

    if (dateTime) {
      customQueryOptions.push({ key: 'dateTime', value: dateTime });
    }

    return (
      newOdata
        .Query()
        .Top(this.pageSize)
        .Skip(this.pageSize * (this.page - 1))
        .OrderBy(this.orderBy) // must be `orderByForDriveItems`
        // .Filter(this.prepareFilter(searchTerm))
        .CustomQueryOptions(customQueryOptions)
        .ExecWithCount()
    );
  }

  getPageOfSiteItems(payload: PageSiteItemsPayload): Observable<number> {
    const newOdata = this.odataFactory.CreateService(`/SiteItems/GetPage`);

    return newOdata
      .Query()
      .Top(this.pageSize)
      .OrderBy(this.orderBy) // must be `orderByForDriveItems`
      .CustomQueryOptions([{ key: 'siteId', value: payload.siteId }])
      .Exec() as unknown as Observable<number>;
  }

  getPageDriveItemsOfSiteLists(payload: PageDriveItemsPayload): Observable<number> {
    const newOdata = this.odataFactory.CreateService(`/SiteDriveItems/GetPage`);

    return newOdata
      .Query()
      .Top(this.pageSize)
      .OrderBy(this.orderBy) // must be `orderByForDriveItems`
      .CustomQueryOptions([
        { key: 'siteBlockId', value: payload.siteBlockId },
        { key: 'parentId', value: payload.parentId },
        { key: 'driveItemId', value: payload.driveItemId }
      ])
      .Exec() as unknown as Observable<number>;
  }

  listItemsOfSiteLists(blockId: string, id: string): Observable<ODataPagedResult<SiteDriveOdataItem>> {
    const newOdata = this.odataFactory.CreateService<SiteDriveOdataItem>(`/SiteLists(${blockId})/ListItems`);

    return (
      newOdata
        .Query()
        .Top(this.pageSize)
        .Skip(this.pageSize * (this.page - 1))
        .OrderBy(null) // must be `orderByGlobal`
        // .Filter(this.prepareFilter(searchTerm))
        .CustomQueryOptions([{ key: 'Id', value: id }])
        .ExecWithCount()
    );
  }

  findDriveItemsOfSiteList(options?: SearchODataOptions): Observable<ODataPagedResult<SiteDriveOdataItem>> {
    const { filter, orderBy, page, pageSize } = options || {};
    const customQueryOptions = [];

    if (this.dateTime()) {
      customQueryOptions.push({ key: 'dateTime', value: this.dateTime() });
    }

    return this.odataSiteDriveItems
      .Query()
      .Top(pageSize || this.pageSize)
      .Skip((pageSize || this.pageSize) * ((page || this.page) - 1))
      .OrderBy(this.orderByForSearch(orderBy)) // this.searchTerm ? this.orderByForDriveItems : this.orderByGlobal
      .Filter(filter || this.filter)
      .CustomQueryOptions(customQueryOptions)
      .ExecWithCount();
  }

  siteItemDetails(id: string): Observable<SiteItemDetails> {
    return this.odata.CustomFunction(id, 'GetDetails').pipe(map((res: HttpResponse<SiteItemDetails>) => res.body));
  }

  siteListsDetails(id: string): Observable<SiteItemDetails> {
    return this.odataSiteLists.CustomFunction(id, 'GetDetails').pipe(map((res: HttpResponse<SiteItemDetails>) => res.body));
  }

  siteDriveItemsDetails(id: string): Observable<SiteItemDetails> {
    const dateTime = this.dateTime();

    return fetchItemDetailsHelper(this.odataSiteDriveItems, { id, dateTime });
  }

  siteListsItemsDetails(id: string): Observable<SiteItemDetails> {
    const dateTime = this.dateTime();

    return fetchItemDetailsHelper(this.odataSiteListsItems, { id, dateTime });
  }

  setAllSitesBackup(value: boolean): Observable<any> {
    return this.odataMethods
      .CustomCollectionAction('SetupAllSitesBackup', { addAllToBackup: value })
      .pipe(map((res: HttpResponse<{ body: any }>) => res.body));
  }

  getAutoAddSiteToBackupStatus(domainId: string): Observable<Site> {
    return this.odataSite.Get(domainId).Exec();
  }

  setAutoAddSiteToBackup(id: string, value: boolean): Observable<{ AutoAddSiteToBackup: boolean }> {
    return this.odataSite.Put({ AutoAddSiteToBackup: value }, id).Exec();
  }

  setupSitesBackup(payload: SetupSitesBackupPayload): Observable<void> {
    return this.odataMethods.CustomCollectionAction(`SetupSitesBackup`, payload);
  }

  restoreSiteItems(data: RestoreSiteItemsPayload): Observable<void> {
    return this.odataMethods.CustomCollectionAction('RestoreSiteItems', data);
  }

  updateFilter(searchObj: SmartSearchModel | SmartSearchModel[]): string {
    const filter: string[] = [];

    if (Array.isArray(searchObj)) {
      for (const item of searchObj) {
        filter.push(this.getFilter(item));
      }
    } else {
      filter.push(this.getFilter(searchObj));
    }

    return filter.length > 0 ? filter.filter(Boolean).join(` ${BooleanOperator.And} `) : '';
  }

  private getFilter(searchObj: SmartSearchModel): string {
    const filter: string[] = [];
    const values = SmartSearchHelper.getValues(searchObj);
    const type = this.getTypeFromSearchObj(searchObj);

    if (searchObj[this.#siteName.tag] && type === SearchType.SiteItems) {
      const value = SmartSearchHelper.getJoinedValue(values, this.#siteName.tag);

      filter.push(containsWrapper(this.#siteName.prop, value));
    }

    if (searchObj[this.#fileName.tag] && type === SearchType.Files) {
      const value = SmartSearchHelper.getJoinedValue(values, this.#fileName.tag);

      filter.push(containsWrapper(this.#fileName.prop, value));
    }

    if (type === SearchType.FilesInSiteItem) {
      const valueMixinName = SmartSearchHelper.getJoinedValue(values, this.#mixinName.tag);
      const valueSiteName = SmartSearchHelper.getJoinedValue(values, this.#siteName.tag);
      const valueFileName = SmartSearchHelper.getJoinedValue(values, this.#fileName.tag);

      searchObj[this.#mixinName.tag] && filter.push(containsWrapper(this.#mixinName.prop, valueMixinName));
      searchObj[this.#siteName.tag] && filter.push(containsWrapper(this.#mixinName.prop, valueSiteName));
      searchObj[this.#fileName.tag] && filter.push(containsWrapper(this.#fileName.prop, valueFileName));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');
      const keys = [this.#fileName.prop];
      const values = keys.map((k) => containsWrapper(k, term));

      filter.push('('.concat(values.join(` ${BooleanOperator.Or} `), ')'));
    }

    return filter.length > 0 ? filter.filter(Boolean).join(` ${BooleanOperator.And} `) : '';
  }

  getTypeFromSearchObj(searchObj: SmartSearchModel): SearchType {
    switch (true) {
      case searchObj[this.#siteName.tag] && !searchObj[this.#fileName.tag]:
        return SearchType.SiteItems;
      case searchObj[this.#fileName.tag] && !searchObj[this.#siteName.tag]:
      case !!searchObj.words?.filter(Boolean):
        return SearchType.Files;
      case !!(searchObj[this.#fileName.tag] && searchObj[this.#siteName.tag]):
      case !!searchObj[this.#mixinName.tag]:
        return SearchType.FilesInSiteItem;
      default:
        return null;
    }
  }

  private orderByForSearch(orderBy: string): string {
    return orderBy === '' ? null : this.orderBy;
  }
}
